import React, { memo, useMemo } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import type { CorkBoardThreadDetail } from '@/types/residents/corkBoard';
import { TagBadge } from './TagBadge';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getAvatarColor } from '@/utils/residents/avatarColors';
import { formatRelativeDate } from '@/utils/common/formatting';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface ThreadHeaderProps {
  thread: CorkBoardThreadDetail;
}

export const ThreadHeader: React.FC<ThreadHeaderProps> = memo(({ thread }) => {
  const { colors, mode } = useThemeColors();
  const avatarColor = useMemo(
    () => getAvatarColor(thread.creator.name, mode),
    [thread.creator.name, mode],
  );
  const hasReplies = thread.replyCount > 0;

  const badgeType: 'announcement' | 'pinned' | null = thread.isAnnouncement
    ? 'announcement'
    : thread.isPinned
    ? 'pinned'
    : null;

  return (
    <View style={styles.container}>
      {/* --- Badge: Announcement or Pinned --- */}
      {badgeType !== null && (
        <View style={styles.badgeWrapper}>
          <View style={styles.badge}>
            {badgeType === 'announcement' ? (
              <RESIDENTS_SVG_COMPONENTS.STAR
                width={12}
                height={12}
                color={PRIMITIVE_COLORS.resioBlue}
              />
            ) : (
              <RESIDENTS_SVG_COMPONENTS.LOCATION
                width={12}
                height={12}
                color={PRIMITIVE_COLORS.resioBlue}
              />
            )}
            <Text style={styles.badgeText}>
              {badgeType === 'announcement' ? 'Announcement' : 'Pinned'}
            </Text>
          </View>
        </View>
      )}

      {/* --- Tags Row --- */}
      {thread.tags.length > 0 && (
        <View style={styles.tagsRow}>
          {thread.tags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} size="medium" />
          ))}
        </View>
      )}

      {/* --- Hero Title --- */}
      <Text style={[styles.title, { color: colors.text.primary }]}>{thread.title}</Text>

      {/* --- Author Strip --- */}
      <View style={styles.authorStrip}>
        {/* Avatar */}
        {thread.creator.photoUrl ? (
          <Image
            source={{ uri: thread.creator.photoUrl }}
            style={styles.avatarImage}
          />
        ) : (
          <View style={[styles.avatar, { backgroundColor: avatarColor.bg }]}>
            <Text style={[styles.avatarInitials, { color: avatarColor.text }]}>
              {thread.creator.initials}
            </Text>
          </View>
        )}

        {/* Stacked name + timestamp */}
        <View style={styles.authorInfo}>
          <View style={styles.nameRow}>
            <Text style={[styles.authorName, { color: colors.text.secondary }]}>{thread.creator.name}</Text>
            {thread.isOwnThread && (
              <View style={styles.authorBadge}>
                <Text style={styles.authorBadgeText}>Author</Text>
              </View>
            )}
          </View>
          <Text style={[styles.timestamp, { color: colors.text.dim }]}>{formatRelativeDate(thread.createdAt)}</Text>
        </View>
      </View>

      {/* --- Stats Row --- */}
      <View style={styles.statsRow}>
        <RESIDENTS_SVG_COMPONENTS.CONVERSATION
          width={14}
          height={14}
          color={hasReplies ? PRIMITIVE_COLORS.resioBlue : colors.text.dimmer}
        />
        <Text style={[styles.statText, { color: colors.text.dimmer }, hasReplies && styles.statTextActive]}>
          {thread.replyCount}
        </Text>
        <Text style={[styles.statDot, { color: colors.text.dimmer }]}>·</Text>
        <RESIDENTS_SVG_COMPONENTS.NOTIFICATION
          width={14}
          height={14}
          color={colors.text.dimmer}
        />
        <Text style={[styles.statText, { color: colors.text.dimmer }]}>{thread.viewCount}</Text>
      </View>
    </View>
  );
});

ThreadHeader.displayName = 'ThreadHeader';

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },

  // --- Badge ---
  badgeWrapper: {
    paddingVertical: 8,
    alignSelf: 'flex-start',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(70,114,254,0.12)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 100,
    alignSelf: 'flex-start',
  },
  badgeText: {
    ...REDESIGN_TYPOGRAPHY.label,
    letterSpacing: 0,
    textTransform: undefined,
    color: PRIMITIVE_COLORS.resioBlue,
  },

  // --- Tags ---
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },

  // --- Hero Title ---
  title: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.3,
    marginBottom: 6, // combined with container gap=10 gives ~16px to author strip
  },

  // --- Author Strip ---
  authorStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.10)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  avatarInitials: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  authorInfo: {
    flex: 1,
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  authorName: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  authorBadge: {
    backgroundColor: 'rgba(70,114,254,0.12)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  authorBadgeText: {
    ...REDESIGN_TYPOGRAPHY.label,
    fontSize: 10,
    letterSpacing: 0,
    textTransform: undefined,
    color: PRIMITIVE_COLORS.resioBlue,
  },
  timestamp: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },

  // --- Stats Row ---
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statText: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  statTextActive: {
    color: PRIMITIVE_COLORS.resioBlue,
  },
  statDot: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
});
